import React, {
    useCallback,
    useState,
    useContext,
    useEffect
} from 'react';
import _ from 'lodash';
import { ModalNext, ModalHeader, ModalBody, ModalFooter, Loader } from '@jutro/components';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ConfigUtil } from 'wni-portals-util-js';
import { WniCEPolicyService } from 'wnice-capability-policy';
import { Button } from '@jutro/legacy/components';
import { WniInputText } from 'wni-common-base-components';
import metadata from './RaiseDisputePopup.metadata.json5';
import messages from '../PolicyDetails/WniPolicyDetails.messages';
import styles from './RaiseDisputePopup.module.scss'

function RaiseDisputePopup(props) {
    const {
        size,
        model,
        actionBtnLabel,
        cancelBtnLabel,
        isOpen,
        onResolve,
        onReject,
        title,
        viewModelService,
        authHeader
    } = props;

    const [showErrors, updateShowErrors] = useState(false);
    const [auditDataVM, updateAuditDataVM] = useState(model);
    const [uploadFiles, updateUploadFiles] = useState();
    const [rowsSelected, updateRowsSelected] = useState([]);
    const [isloading, updateIsloading] = useState(false);
    const [notification, updateNotification] = useState({});

    const {
        isComponentValid,
        onValidate 
    } = useValidation('raiseDisputePopup');

    useEffect(() => {
    }, []);

    const handleValidation = useCallback( () => {
        updateShowErrors(true);
    }, [updateShowErrors]);

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            }

            reader.onerror = reject;

            reader.readAsDataURL(file);
        })
    }

    const submitService = async (res) => {
        const {files} = res
        
        if (!_.isEmpty(files)) {
            const attachments = await Promise.all(
                files.map(async (file) => ({
                    fileName: file.name,
                    fileContent: await fileToBase64(file)
                }))
            )
            _.set(res.auditDataVM, 'attachments', attachments);
        }
        
        // for pc required, should remove these two fields
        delete res.auditDataVM.value.effectiveDate
        delete res.auditDataVM.value.expirationDate

        _.set(res.auditDataVM, 'isCustomerEngage', true);

        updateIsloading(true);
        WniCEPolicyService.raiseDispute(res.auditDataVM.value, authHeader)
            .then(() => {
                const newNotification = {
                    type: 'info',
                    message: messages.raiseDisputeMsg
                }
                updateNotification(newNotification);
                onResolve(newNotification)
                updateIsloading(false);
            })
            .catch((errorRes) => {
                const newNotification = {
                    type: 'error',
                    message: errorRes.baseError
                }
                updateNotification(newNotification);
                onResolve(newNotification)
                updateIsloading(false);
            })
        
    }

    const handleSubmit = () => {
        if (!isComponentValid) {
            handleValidation();
        } else {
            submitService({ files: uploadFiles, auditDataVM: auditDataVM });
        }
    }

    const addClassificationIssues = useCallback(() => {
        const newData = _.cloneDeep(auditDataVM.value.classificationIssues);
        newData.push({ rowIdPath: ConfigUtil.getUuid() });

        const newAuditDataVM = viewModelService.clone(auditDataVM);
        _.set(newAuditDataVM, 'classificationIssues', newData)
        updateAuditDataVM(newAuditDataVM);
    }, [auditDataVM, viewModelService])

    const removeClassificationIssues = () => {
        let newData = _.cloneDeep(auditDataVM.value.classificationIssues);
        newData = _.filter(newData, (item) => {
            const rs = _.find(rowsSelected, (idx) => idx === item.rowIdPath);
            if (rs) {
                return false;
            }
            return true;
        });
        
        const newAuditDataVM = viewModelService.clone(auditDataVM);
        _.set(newAuditDataVM, 'classificationIssues', newData)
        updateAuditDataVM(newAuditDataVM);
        updateRowsSelected([]);
    }

    const updateColomnData = useCallback((targetID, value, path) => {
        const newTableData = _.clone(auditDataVM.value.classificationIssues);
        const changedItem = _.find(newTableData, (item) => {
            return _.get(item, 'rowIdPath') === targetID;
        });
        _.set(changedItem, path, value);

        const newAuditDataVM = viewModelService.clone(auditDataVM);
        _.set(newAuditDataVM, 'classificationIssues', newTableData)
        updateAuditDataVM(newAuditDataVM);
    }, [auditDataVM, viewModelService]);

    const renderTableCell = (item, index, property) => {
        const path = _.get(property, 'path');
        const value = _.get(item, `${path}`);
        const id = path + _.get(item, 'rowIdPath');

        const inputProps = {
            path,
            value,
            onValueChange: (changedValue, changedPath) => updateColomnData(
                _.get(item, 'rowIdPath'), changedValue, changedPath),
            type: 'field',
        };
        const retval = <WniInputText id={id} {...inputProps} />
        return retval;
    }

    const writeValue = useCallback((value, path) => {
        const newAuditDataVM = viewModelService.clone(auditDataVM);
        _.set(newAuditDataVM, path, value);
        updateAuditDataVM(newAuditDataVM);
    }, [auditDataVM, viewModelService]);

    const getOverrideProps = useCallback(() => {
        return {
            '@field': {
                showOptional: false,
                labelPosition: 'left',
                showRequired: true
            },
            auditNotification: {
                visible: !_.isEmpty(notification),
                message: notification.message,
                type: notification.type
            },
            classificationIssuesTable: {
                data: auditDataVM.value.classificationIssues,
                onSelectionChange: (rows) => updateRowsSelected(rows),
                rowIdPath: 'rowIdPath'
            },
            removeClassificationIssues: {
                disabled: _.isEmpty(rowsSelected)
            },
            uploadDocumentsDrop: {
                singleUpload: false,
                uploadConfig: {
                    uploadFiles,
                    updateUploadFiles
                }
            }
            
        }
    }, [auditDataVM.value.classificationIssues, notification, rowsSelected, uploadFiles]);

    const overrideProps = getOverrideProps()
    
    const resolvers = {
        resolveCallbackMap: {
            removeClassificationIssues,
            addClassificationIssues,
            renderTableCell
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
        },
    };

    const readValue = (id, path) => {
        return readViewModelValue(
            metadata.componentContent,
            auditDataVM,
            id,
            path,
            overrideProps
        );
    };

    return (
        <ModalNext isOpen={isOpen} className={size}>
            <ModalHeader title={title} />
            <ModalBody id="raiseDisputePopup">
                {isloading ? (
                    <Loader/>
                ) : (
                    <ViewModelForm
                        uiProps={metadata.componentContent}
                        overrideProps={overrideProps}
                        model={auditDataVM}
                        callbackMap={resolvers.resolveCallbackMap}
                        componentMap={resolvers.resolveComponentMap}
                        classNameMap={resolvers.resolveClassNameMap}
                        resolveValue={readValue}
                        onValueChange={writeValue}
                        onValidationChange={onValidate}
                        showErrors={showErrors}
                    />
                )}
            </ModalBody>
            <ModalFooter>
                <Button onClick={onReject} type="outlined">{cancelBtnLabel}</Button>
                <Button onClick={handleSubmit} disabled={isloading}>{actionBtnLabel}</Button>
            </ModalFooter>
        </ModalNext>
    );
}

RaiseDisputePopup.propTypes = {
   
};
RaiseDisputePopup.defaultProps = {
    size: 'lg'
};
export default RaiseDisputePopup;
